/* eslint-disable no-continue */
/* eslint-disable no-plusplus */
/* eslint-disable no-await-in-loop */
/* eslint-disable no-nested-ternary */
import { useState, useRef, useMemo, useEffect } from 'react';
import { useMedia } from 'react-use';
import { useQuery } from 'react-query';
import { useRouter } from 'next/router';
import { breakpoints } from '@playdapp/ui';
import { formatEther } from '@ethersproject/units';
import { Contract } from '@ethersproject/contracts';
import { MaxUint256 } from '@ethersproject/constants';
import _ from 'lodash';

import getMyItemsData from 'api/graphql/getMyAssets';
import { OrOperationType } from 'api/graphql';
import { getMergeFee, getMergeAvailable } from 'api/merge';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { setOrOperations, setPrevUrl } from 'store/filters';
import { setMergeStatus, fetchMerge } from 'store/merge';
import { getBreakpointQuery, toPascalCase } from 'lib/util';
import { chainIdToMainNetworkName } from 'lib/network';
import contractAddress from 'lib/web3/contract-address';
import useOpenControl from 'hooks/useOpenControl';
import useToastMessage from 'hooks/useToastMessage';
import {
  PLAYDAPP_TOWN_TAB_LIST,
  ASSET_IDS,
  MAX_SELECT_COUNT,
  INIT_DRAGON_R_LIST,
  INIT_TOWN_R_LIST,
  INIT_TOWN_SR_LIST,
  INIT_MERGE_AVAILABLE,
  PURCHASE_LINK,
  PLAYZ_V1_ASSET_ID,
  PLAYZ_V2_ASSET_ID,
  PLAYDAPP_DRAGON_ASSET_ID,
  MERGE_ERROR_TEXT_LIST,
  MERGE_OWNER_CHECK_ERROR_TEXT_LIST,
} from 'data/merge';
import PLA_TOKEN from 'data/contracts/plaToken.json';
import PRESALE_TOKEN from 'data/contracts/presaleToken.json';

import {
  AssetType,
  Tab,
  AssetItem,
  CharacterName,
  Payload,
  Materials,
  MergeAvailable,
  MERGE_ACTIVE_CHAIN_ID,
} from 'types/merge';

import BalanceModal from '@/components/Modal/BalanceModal';
import EmailVerifyModal from '@/components/Modal/EmailVerifyModal';
import Image from '@/components/Image';
import Icon from '@/components/Icon';
import Redirect from '@/components/Redirect';
import MergeConfirmModal from '@/components/Modal/MergeConfirmModal';
import MergeErrorModal from '@/components/Modal/MergeErrorModal';
import LoadingLayer from '../LoadingLayer';

import {
  PageBlock,
  PageWrapper,
  MobileHeader,
  MobileButtonBlock,
  MobileClearButton,
  MobileButton,
  MobileHeaderTitle,
  MergeBlock,
  TabBlock,
  TabItem,
  TabNotification,
  DragonTextBox,
  CharacterBlock,
  CharacterSection,
  CharacterBox,
  CharacterTitleBlock,
  CharacterListBlock,
  Character,
  CharacterCount,
  CharacterButton,
  JokerCountBox,
  JokerCountButton,
  MergeButtonSection,
  MergeButtonImageBox,
  MergeButtonBox,
  SelectedStatusBox,
  SelectedJokerStatusBox,
  CustomButton,
  CostBox,
  ExplainBlock,
  ExplainSection,
  GuideSection,
  GuideBox,
  GuideTitleBlock,
  GuideTitleBox,
  GuideDescBox,
  GuideExploreButtonBox,
  GuideExploreButton,
  ErrorBlock,
  ErrorTitleBlock,
  ErrorDescBlock,
  RefreshButton,
  ClearButton,
} from './style';

type Props = {
  handleToggleMerge: () => void;
};

const Merge = ({ handleToggleMerge }: Props) => {
  const [selectedTab, setSelectedTab] = useState<Tab>('PLAYDAPP_TOWN_R');
  const [normalList, setNormalList] = useState<AssetItem[]>([]);
  const [jokerList, setJokerList] = useState<AssetItem[]>([]);
  const [mergeFee, setMergeFee] = useState<number | null>(null);
  const [selectedTokens, setSelectedTokens] = useState<AssetItem[]>([]);
  const [isJokerSelected, setIsJokerSelected] = useState(false);
  const [isOwnerCheckLoading, setIsOwnerCheckLoading] = useState(false);
  const [ownerTokenIds, setOwnerTokenIds] = useState<string[]>([]);
  const [mergeAvailable, setMergeAvailable] =
    useState<MergeAvailable>(INIT_MERGE_AVAILABLE);
  const [isOwnerCheckFail, setIsOwnerCheckFail] = useState(false);

  const [isNeedPlayzV1Approve, setIsNeedPlayzV1Approve] = useState(false);
  const [isNeedPlayzV2Approve, setIsNeedPlayzV2Approve] = useState(false);
  const [isNeedDragonApprove, setIsNeedDragonApprove] = useState(false);

  const [recaptcha, setRecaptcha] = useState<string | null>(null);

  const { selectedJokerCount, selectedTotalCount, isSubmit } = useMemo(() => {
    const selectedNormalCount = normalList
      ? Object.values(normalList).filter((item) => item.isSelected).length
      : 0;

    const selectedJokerCounts = jokerList.map(
      (item) => item.tokens.filter((item2) => 'isSelected' in item2).length,
    );
    const selectedJokerCount = selectedJokerCounts.length
      ? selectedJokerCounts.reduce((acc, num) => acc + num)
      : 0;

    const selectedTotalCount = selectedNormalCount + selectedJokerCount;

    return {
      selectedJokerCount,
      selectedTotalCount: selectedNormalCount + selectedJokerCount,
      isSubmit: selectedTotalCount === MAX_SELECT_COUNT && !isOwnerCheckLoading,
    };
  }, [normalList, jokerList, isOwnerCheckLoading]);

  const isDragon = useMemo(
    () => selectedTab === 'PLAYDAPP_DRAGON_R',
    [selectedTab],
  );

  const [isBalanceModalOpen, handleBalanceModalOpen] = useOpenControl();
  const [isEmailModalOpen, handleEmailModalOpen] = useOpenControl();
  const [isMergeModalOpen, handleMergeModalOpen] = useOpenControl();
  const [isMergeErrorModalOpen, handleMergeErrorModalOpen] = useOpenControl();

  const { handleUpdateToast, handleCloseToast } = useToastMessage({
    position: 'top',
    duration: 5000,
  });

  const isMdDownScreen = useMedia(getBreakpointQuery(breakpoints.down('md')));

  const pageBlock = useRef<HTMLDivElement | null>(null);

  const { account, activeChainId, balance, library } = useAppSelector(
    ({ wallet }) => wallet,
  );
  const { isVerified } = useAppSelector(({ user }) => user);
  const { mergeStatus } = useAppSelector(({ merge }) => merge);

  const router = useRouter();
  const dispatch = useAppDispatch();

  const networkName = useMemo(
    () => (activeChainId ? chainIdToMainNetworkName[activeChainId] : ''),
    [activeChainId],
  );

  const PLA_CONTRACT =
    library &&
    new Contract(
      contractAddress.token.pla[
        (activeChainId as MERGE_ACTIVE_CHAIN_ID) || 137
      ],
      PLA_TOKEN,
      library.getSigner(),
    );

  const PLAYZ_V1_CONTRACT = useMemo(() => {
    if (!activeChainId) return null;

    return library && networkName === 'polygon'
      ? new Contract(
          contractAddress.token.playzV1[
            (activeChainId as MERGE_ACTIVE_CHAIN_ID) || 137
          ],
          PRESALE_TOKEN,
          library.getSigner(),
        )
      : null;
  }, [library, networkName]);

  const PLAYZ_V2_CONTRACT = useMemo(() => {
    if (!activeChainId) return null;

    return library && networkName === 'polygon'
      ? new Contract(
          contractAddress.token.playzV2[
            (activeChainId as MERGE_ACTIVE_CHAIN_ID) || 137
          ],
          PRESALE_TOKEN,
          library.getSigner(),
        )
      : null;
  }, [library, networkName]);

  const DRAGON_CONTRACT = useMemo(() => {
    if (!activeChainId) return null;

    return library && networkName === 'polygon'
      ? new Contract(
          contractAddress.token.playdappDragon[
            (activeChainId as MERGE_ACTIVE_CHAIN_ID) || 137
          ],
          PRESALE_TOKEN,
          library.getSigner(),
        )
      : null;
  }, [library, networkName]);

  const PLAYDAPP_CONTRACT =
    contractAddress.token.playdapp[
      (activeChainId as MERGE_ACTIVE_CHAIN_ID) || 137
    ];

  const token = localStorage.getItem('PDMP_AUTH_TOKEN');

  const handleOwnerOf = async (tokenId: number, assetTitle: string) => {
    if (!tokenId || !library) return null;

    let contract = PLAYZ_V1_CONTRACT;

    if (assetTitle === 'PLAYZ V2') {
      contract = PLAYZ_V2_CONTRACT;
    } else if (assetTitle === 'DRGNZ') {
      contract = DRAGON_CONTRACT;
    }

    if (!contract) return '';

    const ownerAddress = await contract.ownerOf(tokenId);
    return ownerAddress;
  };

  const fetchList = async () => {
    if (!account || !activeChainId) {
      throw new Error('No account or chainId');
    }

    const PLAYDAPP_TOWN_ASSET_IDS =
      ASSET_IDS.PLAYDAPP_TOWN[activeChainId as MERGE_ACTIVE_CHAIN_ID];
    const PLAYDAPP_DRAGON_ASSET_IDS =
      ASSET_IDS.PLAYDAPP_DRAGON[activeChainId as MERGE_ACTIVE_CHAIN_ID];

    const res = await getMyItemsData({
      from: 0,
      count: 9999,
      andOperations: [
        { key: 'order.sale', value: '$not_exist' },
        { key: 'order.auction', value: '$not_exist' },
        { key: 'owner', value: account },
        { key: 'network', value: chainIdToMainNetworkName[activeChainId] },
      ],
      orOperations: [
        [...PLAYDAPP_TOWN_ASSET_IDS, ...PLAYDAPP_DRAGON_ASSET_IDS],
        [
          { key: 'attributes.Grade.keyword', value: 'R' },
          { key: 'attributes.Grade.keyword', value: 'SR' },
        ],
      ],
      sortBy: 'Transfer',
      sortAscending: false,
    });

    const { assets } = res;

    const playdappDragonAssetId = Number(PLAYDAPP_DRAGON_ASSET_IDS[0].value);

    const playdappTownList = assets.filter(
      (item) => item.assetId !== playdappDragonAssetId,
    );
    const playdappDragonList = assets.filter(
      (item) => item.assetId === playdappDragonAssetId,
    );

    const playdappTownGroupBy = _.groupBy(playdappTownList, 'name');
    const playdappDragonGroupBy = _.groupBy(playdappDragonList, 'name');

    const originalDragonR = INIT_DRAGON_R_LIST.map((item) => ({
      ...item,
      isSelected: false,
      count:
        item.apiName in playdappDragonGroupBy
          ? playdappDragonGroupBy[item.apiName].length
          : 0,
      tokens:
        item.apiName in playdappDragonGroupBy
          ? playdappDragonGroupBy[item.apiName]
          : [],
    }));

    const originalTownR = {
      normal: INIT_TOWN_R_LIST.map((item) => ({
        ...item,
        image: `${item.image}-r.png`,
        isSelected: false,
        count:
          item.apiName in playdappTownGroupBy
            ? playdappTownGroupBy[item.apiName].length
            : 0,
        tokens:
          item.apiName in playdappTownGroupBy
            ? playdappTownGroupBy[item.apiName]
            : [],
      })),
      joker: [...originalDragonR],
    };

    const originalTownSR = {
      normal: INIT_TOWN_SR_LIST.map((item) => ({
        ...item,
        image: `${item.image}-sr.png`,
        isSelected: false,
        count:
          item.apiName in playdappTownGroupBy
            ? playdappTownGroupBy[item.apiName].length
            : 0,
        tokens:
          item.apiName in playdappTownGroupBy
            ? playdappTownGroupBy[item.apiName]
            : [],
      })),
      joker: [
        {
          name: 'demetalir',
          image: '/merge/demetalir-sr.png',
          isSelected: false,
          count:
            'Demetalir SR' in playdappDragonGroupBy
              ? playdappDragonGroupBy['Demetalir SR'].length
              : 0,
          tokens:
            'Demetalir SR' in playdappDragonGroupBy
              ? playdappDragonGroupBy['Demetalir SR']
              : [],
        },
      ],
    };

    const newNormalList = isDragon
      ? originalDragonR
      : selectedTab === 'PLAYDAPP_TOWN_R'
      ? originalTownR.normal
      : originalTownSR.normal;

    const newJokerList = isDragon
      ? []
      : selectedTab === 'PLAYDAPP_TOWN_R'
      ? originalTownR.joker
      : originalTownSR.joker;

    setNormalList(newNormalList);
    setJokerList(newJokerList);

    return { originalDragonR, originalTownR, originalTownSR };
  };

  const { isLoading, data, error, refetch } = useQuery(
    ['merge', account],
    fetchList,
    {
      enabled: !!account && !!activeChainId,
      refetchOnWindowFocus: false,
    },
  );

  const handleClear = (tab: Tab) => {
    setSelectedTab(tab);
    setSelectedTokens([]);
    setIsJokerSelected(false);
    setIsNeedPlayzV1Approve(false);
    setIsNeedPlayzV2Approve(false);
    setIsNeedDragonApprove(false);
    setOwnerTokenIds([]);
    setRecaptcha(null);
  };

  const handleTabClick = (tab: Tab, isClear?: boolean) => () => {
    if (!data || (tab === selectedTab && !isClear)) return;

    handleClear(tab);

    if (tab === 'PLAYDAPP_TOWN_R') {
      setNormalList(data.originalTownR.normal);
      setJokerList(data.originalTownR.joker);
    }

    if (tab === 'PLAYDAPP_TOWN_SR') {
      setNormalList(data.originalTownSR.normal);
      setJokerList(data.originalTownSR.joker);
    }

    if (tab === 'PLAYDAPP_DRAGON_R') {
      setNormalList(data.originalDragonR);
      setJokerList([]);
    }
  };

  const handleMergeClose = () => {
    if (pageBlock.current) {
      pageBlock.current.style.left = '100%';
    }

    setTimeout(() => {
      handleToggleMerge();

      if ('merge' in router.query) {
        router.push('/my-page/items');
      }
    }, 500);
  };

  const handlePurchaseClick =
    (
      assetTitle: 'PLAYDAPP_DRAGON' | 'PLAYDAPP_TOWN',
      base: string,
      grade: 'R' | 'SR',
    ) =>
    () => {
      const link =
        PURCHASE_LINK[assetTitle][activeChainId as MERGE_ACTIVE_CHAIN_ID];

      const PLAYDAPP_TOWN_ASSET_IDS =
        ASSET_IDS.PLAYDAPP_TOWN[activeChainId as MERGE_ACTIVE_CHAIN_ID];
      const PLAYDAPP_DRAGON_ASSET_IDS =
        ASSET_IDS.PLAYDAPP_DRAGON[activeChainId as MERGE_ACTIVE_CHAIN_ID];

      const orOperations: OrOperationType[][] = [
        assetTitle === 'PLAYDAPP_TOWN'
          ? PLAYDAPP_TOWN_ASSET_IDS
          : PLAYDAPP_DRAGON_ASSET_IDS,
        [
          {
            key: 'attributes.Base.keyword',
            value:
              assetTitle === 'PLAYDAPP_TOWN'
                ? base.toUpperCase()
                : toPascalCase(base),
          },
        ],
        [
          {
            key: 'attributes.Grade.keyword',
            value: grade,
          },
        ],
      ];

      dispatch(setPrevUrl('merge'));
      dispatch(setOrOperations(orOperations));

      setTimeout(() => router.push(link), 50);
    };

  const handleOwnerCheckFail = () => {
    setIsOwnerCheckFail(true);
    handleMergeErrorModalOpen(true);
  };

  const handleOwnerCheck = async (
    assetList: AssetItem[],
    selectedAssetName: string,
    needCount: number,
  ): Promise<boolean> => {
    setIsOwnerCheckLoading(true);
    const asset = assetList.find((item) => item.name === selectedAssetName);

    if (!asset || !asset.tokens?.length) return false;

    let newOwnerTokenIds: string[] = [];

    for (let i = 0; i < asset.tokens.length; i++) {
      const { assetTitle, tokenId } = asset.tokens[i];

      if (!assetTitle || !tokenId) {
        continue;
      }

      const ownerAddress = await handleOwnerOf(
        parseInt(tokenId, 10),
        assetTitle,
      );

      if (newOwnerTokenIds.length === needCount) {
        break;
      }

      if (account === ownerAddress) {
        newOwnerTokenIds = newOwnerTokenIds.concat(tokenId);
      }
    }

    const totalOwnerTokenIds = [...newOwnerTokenIds, ...ownerTokenIds];

    setOwnerTokenIds(_.uniq(totalOwnerTokenIds));
    setIsOwnerCheckLoading(false);

    return newOwnerTokenIds.length === needCount;
  };

  const handleSelectClick = async (
    name: CharacterName,
    isAdd: boolean,
    isJoker: boolean,
  ) => {
    // 선택한 개수 총합이랑 최대선택개수랑 같고 plus면 리턴
    if (selectedTotalCount === MAX_SELECT_COUNT && isAdd) return;

    if (isJoker) {
      const target = jokerList.find((item) => item.name === name);

      if (!target) return;

      const selectedCount = target?.tokens.filter(
        (item) => 'isSelected' in item,
      ).length;

      // 선택한 카운트가 0이고 마이너스 일 경우 리턴
      if (!selectedCount && !isAdd) return;

      // 선택한 조커 총합이 4보다 크고 plus일 경우 (조커는 최대 5개 선택 가능)
      if (selectedJokerCount > 4 && isAdd) return;

      // 남은 카운트가 0이고 플러스 일 경우 리턴
      if (!target.count && isAdd) return;

      if (isAdd) {
        const ownerCheck = await handleOwnerCheck(
          jokerList,
          name,
          selectedCount + 1,
        );

        if (!ownerCheck) {
          handleOwnerCheckFail();
          return;
        }
      }

      let isChange = false;
      const newTokens = target.tokens.map((item) => {
        // plus인 경우 isSelected가 없는 token에 하나만 isSelected 추가
        if (!isChange && isAdd && !Object.keys(item).includes('isSelected')) {
          isChange = true;

          return {
            ...item,
            isSelected: true,
          };
        }

        // minus인 경우 첫번째로 isSelected가 있는 token의 isSelected를 삭제
        if (!isChange && !isAdd && Object.keys(item).includes('isSelected')) {
          isChange = true;
          delete item.isSelected;

          return {
            ...item,
          };
        }

        return {
          ...item,
        };
      });

      setJokerList((prev) =>
        prev.map((item) => ({
          ...item,
          isSelected: name === item.name ? selectedCount > 0 : item.isSelected,
          count:
            name === item.name
              ? isAdd
                ? item.count - 1
                : item.count + 1
              : item.count,
          tokens: name === item.name ? [...newTokens] : item.tokens,
        })),
      );
    }

    if (!isJoker) {
      if (isAdd) {
        const ownerCheck = await handleOwnerCheck(normalList, name, 1);

        if (!ownerCheck) {
          handleOwnerCheckFail();
          return;
        }
      }

      setNormalList((prev) =>
        prev.map((item) => ({
          ...item,
          isSelected: name === item.name ? !item.isSelected : item.isSelected,
          count:
            name === item.name
              ? isAdd
                ? item.count - 1
                : item.count + 1
              : item.count,
        })),
      );
    }

    const totalCount = isAdd ? selectedTotalCount + 1 : selectedTotalCount - 1;

    if (!isOwnerCheckLoading && totalCount === MAX_SELECT_COUNT) {
      handleUpdateToast(
        `YOU HAVE SELECTED ALL ${MAX_SELECT_COUNT} NFTs. YOU ARE NOW READY TO MERGE!`,
        'success',
        true,
      );
    }
  };

  const fetchMergeFee = async () => {
    try {
      const res = await getMergeFee();

      if (res.status !== 200) {
        throw new Error('Fee Error');
      }

      setMergeFee(Number(res.data));
    } catch (e) {
      console.error(e);
      setMergeFee(-1);
    }
  };

  const fetchMergeAvailable = async () => {
    try {
      const res = await getMergeAvailable();

      if (res.status !== 200) {
        throw new Error('Available Error');
      }

      setMergeAvailable({ ...res.data });
    } catch (e) {
      console.error(e);
      setMergeAvailable(INIT_MERGE_AVAILABLE);
    }
  };

  const handleSubmit = async () => {
    if (!isSubmit) return;

    handleCloseToast();

    try {
      if (
        !balance ||
        !mergeFee ||
        !activeChainId ||
        !PLAYZ_V1_CONTRACT ||
        !PLAYZ_V2_CONTRACT ||
        !DRAGON_CONTRACT ||
        !ownerTokenIds.length
      ) {
        throw new Error('An unexpected error occurred');
      }

      if (!isVerified) {
        handleEmailModalOpen(true);
        return;
      }

      if (balance.pla < mergeFee) {
        handleBalanceModalOpen(true);
        return;
      }

      // 선택한 normal, joker 토큰 전부 담을 변수
      let selectedAssetList: AssetItem[] = [];

      // normal 토큰 담기
      const selectedNormalList = normalList.filter((item) => item.isSelected);

      if (selectedNormalList.length) {
        selectedAssetList = selectedNormalList.map((item) => {
          const findOwnerToken = item.tokens.find((token) =>
            ownerTokenIds.includes(token.tokenId),
          );

          return {
            ...item,
            tokens:
              item.tokens.length && findOwnerToken ? [findOwnerToken] : [],
          };
        });
      }

      // joker 토큰 담기
      const selectedJokerList = jokerList.filter(
        (item) => item.tokens.filter((item2) => item2.isSelected).length,
      );

      if (selectedJokerList.length) {
        const selectedJokerParseList = selectedJokerList.map((item) => {
          const selectedJokerTokens = item.tokens.filter(
            (item2) =>
              item2.isSelected && ownerTokenIds.includes(item2.tokenId),
          );

          return {
            ...item,
            tokens: [...selectedJokerTokens],
          };
        });
        selectedAssetList = [...selectedAssetList, ...selectedJokerParseList];
        setIsJokerSelected(true);
      }

      let selectedTokenCount = 0;

      selectedAssetList.forEach((asset) => {
        selectedTokenCount += asset.tokens.length;
      });

      if (selectedTokenCount !== MAX_SELECT_COUNT) {
        console.error(
          `fewer than ${MAX_SELECT_COUNT} tokens in your possession`,
        );
        handleMergeErrorModalOpen(true);
        return;
      }

      setSelectedTokens(selectedAssetList);

      if (selectedNormalList.length) {
        const playzV1Check = selectedNormalList.find(
          (item) =>
            item.tokens[0].assetId !==
              PLAYZ_V1_ASSET_ID[activeChainId as MERGE_ACTIVE_CHAIN_ID] &&
            item.tokens[0].assetId !== 4,
        );

        const playzV2Check = selectedNormalList.find(
          (item) =>
            item.tokens[0].assetId ===
            PLAYZ_V2_ASSET_ID[activeChainId as MERGE_ACTIVE_CHAIN_ID],
        );

        // 선택한 토큰에 Playz V1 있으면 권한 체크
        if (playzV1Check) {
          const playzV1IsApproved = await PLAYZ_V1_CONTRACT.isApprovedForAll(
            account,
            PLAYDAPP_CONTRACT,
          );
          setIsNeedPlayzV1Approve(!playzV1IsApproved);
        }

        // 선택한 토큰에 Playz V2 있으면 권한 체크
        if (playzV2Check) {
          const playzV2IsApproved = await PLAYZ_V2_CONTRACT.isApprovedForAll(
            account,
            PLAYDAPP_CONTRACT,
          );
          setIsNeedPlayzV2Approve(!playzV2IsApproved);
        }
      }

      // Dragon R > SR 일 경우
      const dragonCheck = selectedNormalList.find(
        (item) =>
          item.tokens[0].assetId ===
          PLAYDAPP_DRAGON_ASSET_ID[activeChainId as MERGE_ACTIVE_CHAIN_ID],
      );

      // Dragon 있으면 권한 체크
      if (dragonCheck || selectedJokerList.length) {
        const dragonIsApproved = await DRAGON_CONTRACT.isApprovedForAll(
          account,
          PLAYDAPP_CONTRACT,
        );
        setIsNeedDragonApprove(!dragonIsApproved);
      }

      handleMergeModalOpen(true);
    } catch (e) {
      console.error(e);
      handleMergeErrorModalOpen(true);
    }
  };

  const handleFetchMerge = async () => {
    dispatch(setMergeStatus('loading'));
    handleMergeModalOpen(false);

    try {
      if (
        !mergeFee ||
        !activeChainId ||
        !library ||
        !PLA_CONTRACT ||
        !PLAYZ_V1_CONTRACT ||
        !PLAYZ_V2_CONTRACT ||
        !DRAGON_CONTRACT ||
        !recaptcha
      ) {
        throw new Error('An unexpected error occurred');
      }

      // PLA 권한 체크
      const resAllowance = await PLA_CONTRACT.allowance(
        account,
        PLAYDAPP_CONTRACT,
      );

      if (
        parseFloat(formatEther(resAllowance)) < parseFloat(mergeFee.toString())
      ) {
        const txApprove = await PLA_CONTRACT.approve(
          PLAYDAPP_CONTRACT,
          MaxUint256,
        );
        await txApprove.wait();
      }

      // Playz V1 권한 체크
      if (isNeedPlayzV1Approve) {
        const txApprove = await PLAYZ_V1_CONTRACT.setApprovalForAll(
          PLAYDAPP_CONTRACT,
          isNeedPlayzV1Approve,
        );
        await txApprove.wait();
      }

      // Playz V2 권한 체크
      if (isNeedPlayzV2Approve) {
        const txApprove = await PLAYZ_V2_CONTRACT.setApprovalForAll(
          PLAYDAPP_CONTRACT,
          isNeedPlayzV2Approve,
        );
        await txApprove.wait();
      }

      // Dragon 권한 체크
      if (isNeedDragonApprove) {
        const txApprove = await DRAGON_CONTRACT.setApprovalForAll(
          PLAYDAPP_CONTRACT,
          isNeedDragonApprove,
        );
        await txApprove.wait();
      }

      const materials: Materials = {
        Playz: [],
        PlayzV2: [],
        Dragon: [],
      };

      selectedTokens.forEach((item) => {
        const tokensGroupBy = _.groupBy(item.tokens, 'assetTitle');

        Object.keys(tokensGroupBy).forEach((key) => {
          const tokenIds: string[] = tokensGroupBy[key].map(
            (token: AssetType) => token.tokenId,
          );

          if (key === 'PLAYZ V2' && materials.PlayzV2) {
            materials.PlayzV2 = [...materials.PlayzV2, ...tokenIds];
          } else if (key === 'DRGNZ' && materials.Dragon) {
            materials.Dragon = [...materials.Dragon, ...tokenIds];
          } else if (materials.Playz) {
            materials.Playz = [...materials.Playz, ...tokenIds];
          }
        });
      });

      if (!materials.Playz?.length) delete materials.Playz;
      if (!materials.PlayzV2?.length) delete materials.PlayzV2;
      if (!materials.Dragon?.length) delete materials.Dragon;

      const payload: Payload = {
        eventType: isJokerSelected ? 'Joker' : 'Normal',
        materials,
        recaptcha,
      };

      dispatch(fetchMerge(payload));
    } catch (e) {
      console.error(e);
      handleMergeErrorModalOpen(true);
    }
  };

  const handleReCAPTCHA = (token: string | null) => {
    setRecaptcha(token);
  };

  useEffect(() => {
    fetchMergeFee();
    fetchMergeAvailable();
  }, [token]);

  useEffect(() => {
    if (mergeStatus === 'success') {
      handleClear(selectedTab);
      refetch();
      fetchMergeAvailable();
    }
  }, [mergeStatus, selectedTab, refetch]);

  useEffect(() => {
    dispatch(setPrevUrl('collection'));
  }, [dispatch]);

  useEffect(() => {
    if (networkName !== 'polygon') router.push('/my-page/items');
  }, [networkName]);

  return (
    <PageBlock ref={pageBlock}>
      <MobileHeader>
        <MobileHeaderTitle>
          <span>Merge &gt; Upgrade</span>
        </MobileHeaderTitle>
        <Icon name="close-circle" size="32px" onClick={handleMergeClose} />
      </MobileHeader>
      <MobileButtonBlock>
        <MobileClearButton
          color="purple"
          onClick={handleTabClick(selectedTab, true)}
        >
          Clear
        </MobileClearButton>
        <MobileButton
          disabled={!isSubmit || mergeStatus === 'loading'}
          onClick={handleSubmit}
        >
          <span>
            {mergeStatus === 'loading' ? (
              'Merging...'
            ) : (
              <>
                Merge NFT ({selectedTotalCount}/{MAX_SELECT_COUNT})
              </>
            )}
          </span>
        </MobileButton>
      </MobileButtonBlock>
      <PageWrapper>
        <MergeBlock>
          <TabBlock>
            {PLAYDAPP_TOWN_TAB_LIST.map((item) => (
              <TabItem
                key={item.tab}
                isSelected={selectedTab === item.tab}
                onClick={handleTabClick(item.tab)}
              >
                {item.tab === 'PLAYDAPP_TOWN_R' && mergeAvailable.playz.R && (
                  <TabNotification />
                )}
                {item.tab === 'PLAYDAPP_TOWN_SR' && mergeAvailable.playz.SR && (
                  <TabNotification />
                )}
                {item.text}
              </TabItem>
            ))}
            <TabItem
              isSelected={isDragon}
              onClick={handleTabClick('PLAYDAPP_DRAGON_R')}
            >
              {mergeAvailable.dragon.R && <TabNotification />}
              <DragonTextBox>
                <p>R &gt; SR</p>
                <p className="light">(Dragon)</p>
              </DragonTextBox>
            </TabItem>
          </TabBlock>

          <CharacterBlock isDragon={isDragon}>
            {isLoading && <LoadingLayer />}

            {!isLoading && error && (
              <ErrorBlock>
                <Icon
                  name="tx-error"
                  width={isMdDownScreen ? '48px' : '64px'}
                  height={isMdDownScreen ? '42.68px' : '56.9px'}
                />
                <ErrorTitleBlock>
                  <span>Error</span>
                </ErrorTitleBlock>
                <ErrorDescBlock>
                  <p>Please refresh data or reconnect wallet.</p>
                </ErrorDescBlock>
                <RefreshButton variant="outline" onClick={() => refetch()}>
                  <Icon
                    name="refresh-primary"
                    width={isMdDownScreen ? '11.42px' : '17.13px'}
                    height={isMdDownScreen ? '13.34px' : '20px'}
                  />
                  <span>Refresh</span>
                </RefreshButton>
              </ErrorBlock>
            )}

            {!isLoading && !error && (
              <CharacterSection isDragon={isDragon}>
                <CharacterBox>
                  <CharacterTitleBlock>
                    <span>PLAYDAPP {isDragon ? 'DRAGON' : 'TOWN'}</span>
                  </CharacterTitleBlock>
                  <CharacterListBlock
                    isSrPlaydappTown={selectedTab === 'PLAYDAPP_TOWN_SR'}
                    isDragon={isDragon}
                  >
                    {normalList &&
                      normalList.map((item, idx) => {
                        const { name, count, image, isSelected } = item;
                        const isPurchase = !isSelected && !count;

                        return (
                          <div key={`playdapp-town-character-${idx}`}>
                            <Character
                              isPurchase={isPurchase}
                              isCursorPointer={!isPurchase}
                              onClick={() =>
                                !isPurchase &&
                                handleSelectClick(
                                  name as CharacterName,
                                  !isSelected,
                                  false,
                                )
                              }
                            >
                              {!isPurchase && (
                                <CharacterCount>{count}</CharacterCount>
                              )}
                              <Image
                                prefix="s3"
                                src={image}
                                width="132px"
                                height="132px"
                                layout="responsive"
                                alt=""
                              />
                              {isSelected && (
                                <div className="selected">
                                  <Icon
                                    name="check-awesome"
                                    width="53.33px"
                                    height="36.67px"
                                  />
                                </div>
                              )}
                            </Character>
                            {isPurchase ? (
                              <CharacterButton
                                variant="outline"
                                color="blue"
                                onClick={handlePurchaseClick(
                                  isDragon
                                    ? 'PLAYDAPP_DRAGON'
                                    : 'PLAYDAPP_TOWN',
                                  name,
                                  selectedTab === 'PLAYDAPP_TOWN_SR'
                                    ? 'SR'
                                    : 'R',
                                )}
                              >
                                Purchase
                              </CharacterButton>
                            ) : (
                              <CharacterButton
                                variant="outline"
                                isActive={isSelected}
                                disabled={
                                  !isSelected &&
                                  (selectedTotalCount === 6 || !!mergeStatus)
                                }
                                onClick={() =>
                                  handleSelectClick(
                                    name as CharacterName,
                                    !isSelected,
                                    false,
                                  )
                                }
                              >
                                Select{isSelected && 'ed'}
                              </CharacterButton>
                            )}
                          </div>
                        );
                      })}
                  </CharacterListBlock>
                </CharacterBox>

                {selectedTab !== 'PLAYDAPP_DRAGON_R' && (
                  <CharacterBox>
                    <CharacterTitleBlock>
                      <span>PLAYDAPP DRAGON</span>
                      <span className="joker">JOKER</span>
                    </CharacterTitleBlock>
                    <CharacterListBlock
                      isSingle={selectedTab === 'PLAYDAPP_TOWN_SR'}
                    >
                      {jokerList &&
                        jokerList.map((item, idx) => {
                          const { name, count, image } = item;
                          const selectedCount = item.tokens.filter(
                            (item2) => 'isSelected' in item2,
                          ).length;
                          const isPurchase = !selectedCount && !count;

                          return (
                            <div key={`playdapp-town-character-${idx}`}>
                              <Character isPurchase={isPurchase}>
                                {!isPurchase && (
                                  <CharacterCount>{count}</CharacterCount>
                                )}
                                <Image
                                  prefix="s3"
                                  src={image}
                                  width="132px"
                                  height="132px"
                                  layout="responsive"
                                  alt=""
                                />
                                {selectedCount > 0 && (
                                  <div className="selected">
                                    <Icon
                                      name="check-awesome"
                                      width="53.33px"
                                      height="36.67px"
                                    />
                                  </div>
                                )}
                              </Character>
                              {isPurchase ? (
                                <Redirect>
                                  <CharacterButton
                                    variant="outline"
                                    color="blue"
                                    onClick={handlePurchaseClick(
                                      'PLAYDAPP_DRAGON',
                                      name,
                                      selectedTab === 'PLAYDAPP_TOWN_R'
                                        ? 'R'
                                        : 'SR',
                                    )}
                                  >
                                    Purchase
                                  </CharacterButton>
                                </Redirect>
                              ) : (
                                <JokerCountBox>
                                  <JokerCountButton
                                    variant="outline"
                                    disabled={
                                      !selectedCount &&
                                      (selectedTotalCount === 6 ||
                                        !!mergeStatus)
                                    }
                                    onClick={() =>
                                      handleSelectClick(
                                        name as CharacterName,
                                        false,
                                        true,
                                      )
                                    }
                                  >
                                    -
                                  </JokerCountButton>
                                  <span className="number">
                                    {selectedCount}
                                  </span>
                                  <JokerCountButton
                                    variant="outline"
                                    disabled={
                                      selectedTotalCount === 6 ||
                                      selectedJokerCount > 4 ||
                                      count === 0 ||
                                      !!mergeStatus
                                    }
                                    onClick={() =>
                                      handleSelectClick(
                                        name as CharacterName,
                                        true,
                                        true,
                                      )
                                    }
                                  >
                                    +
                                  </JokerCountButton>
                                </JokerCountBox>
                              )}
                            </div>
                          );
                        })}
                    </CharacterListBlock>
                  </CharacterBox>
                )}
              </CharacterSection>
            )}

            <MergeButtonSection isDragon={isDragon}>
              <div className="merge-button-bg01" />
              <div className="merge-button-bg02" />
              <ClearButton onClick={handleTabClick(selectedTab, true)}>
                Clear
              </ClearButton>
              <MergeButtonImageBox selectedTab={selectedTab} />
              <MergeButtonBox>
                <SelectedStatusBox>
                  <span className="strong">{selectedTotalCount}</span>
                  <span className="max">
                    &nbsp;/&nbsp;{MAX_SELECT_COUNT}&nbsp;
                  </span>
                  <span>ITEMS</span>
                </SelectedStatusBox>
                {selectedTab !== 'PLAYDAPP_DRAGON_R' && (
                  <SelectedJokerStatusBox>
                    (<span className="strong">&nbsp;{selectedJokerCount}</span>
                    <span>&nbsp;Joker</span>)
                  </SelectedJokerStatusBox>
                )}
                <CustomButton
                  disabled={!isSubmit || mergeStatus === 'loading'}
                  onClick={handleSubmit}
                >
                  <span>
                    {mergeStatus === 'loading' ? 'Merging...' : 'Merge NFT'}
                  </span>
                </CustomButton>

                <CostBox>
                  <span>
                    Merge Cost : {mergeFee && mergeFee > 0 && `${mergeFee} PLA`}
                  </span>
                </CostBox>
              </MergeButtonBox>
            </MergeButtonSection>
          </CharacterBlock>
        </MergeBlock>
        <ExplainBlock>
          <ExplainSection>
            <p className="title">What is Merge?</p>
            <p className="desc">
              Merge makes your NFTs more valuable.
              <br />
              All PLAYZ NFT have a rarity rank. With Merge, you can upgrade them
              into higher ranks, all the way to SSR!
            </p>
            <p className="title">Why Should I Merge My NFT?</p>
            <p className="desc">
              PlayDapp’sNFTs are intended to be interoperable. That ’s our
              mission, and we are building a marketplace that connects different
              games, by letting you seamlessly use and trade your NFTs.You can
              now use PlayDapp Town NFTs for staking in Along with the Gods
              (AWTG) via Item Manager.
            </p>
            <p className="title">How to start merging?</p>
            <p className="desc">
              The merge requires 6 R or SR rank NFTs. Combine these into one
              higher rank NFT (ex. R&nbsp;&gt;&nbsp;SR, or
              SR&nbsp;&gt;&nbsp;SSR) as shown above.
            </p>
            <p className="title">PlayDapp Dragon NFT</p>
            <p className="desc">
              PlayDapp Dragon NFTs are special, because they can be substituted
              for PlayDapp NFTs of equivalent rank when merging.
            </p>
          </ExplainSection>
          <GuideSection>
            <GuideBox>
              <Redirect to="https://medium.com/playdappgames/along-with-the-gods-play-to-earn-2-0-guide-82fd455cfd02">
                <GuideTitleBlock>
                  <GuideTitleBox>
                    <span>AWTG guide</span>
                  </GuideTitleBox>
                  <GuideDescBox>
                    <span>
                      Find out what is AWTG and how PlayDapp Town NFTs are used
                    </span>
                  </GuideDescBox>
                </GuideTitleBlock>
                <Image
                  prefix="s3"
                  src="/merge/awtg-guide3.png"
                  width="72px"
                  height="72px"
                  alt="awtg-guide"
                  layout="fixed"
                />
              </Redirect>
            </GuideBox>
            <GuideBox>
              <Redirect to="https://medium.com/playdappgames/along-with-the-gods-play-to-earn-2-0-staking-guidelines-d9bded4b8665">
                <GuideTitleBlock>
                  <GuideTitleBox>
                    <span>Staking guide</span>
                  </GuideTitleBox>
                  <GuideDescBox>
                    <span>Discover more about Item Manager and Staking</span>
                  </GuideDescBox>
                </GuideTitleBlock>
                <Image
                  prefix="s3"
                  src="/merge/staking-guide3.png"
                  width="72px"
                  height="72px"
                  alt="staking-guide"
                  layout="fixed"
                />
              </Redirect>
            </GuideBox>
            <GuideBox isExplore>
              <Redirect to="/collection/polygon/1">
                <GuideTitleBlock isExplore>
                  <GuideTitleBox>
                    <span>Don’t have NFTs to merge?</span>
                  </GuideTitleBox>
                  <GuideDescBox>
                    <span>
                      Don’t worry! Use the PlayDapp Marketplace to get any NFTs
                      you’re missing!
                    </span>
                  </GuideDescBox>
                </GuideTitleBlock>
                <GuideExploreButtonBox>
                  <GuideExploreButton>
                    <Redirect to="/collection/polygon/1">Explore</Redirect>
                  </GuideExploreButton>
                </GuideExploreButtonBox>
              </Redirect>
            </GuideBox>
          </GuideSection>
        </ExplainBlock>
      </PageWrapper>

      {isMergeModalOpen && (
        <MergeConfirmModal
          isOpen={isMergeModalOpen}
          selectedTab={selectedTab}
          selectedTokens={selectedTokens}
          isApproved={
            isNeedPlayzV1Approve || isNeedPlayzV2Approve || isNeedDragonApprove
          }
          recaptcha={recaptcha}
          handleOpen={handleMergeModalOpen}
          handleFetchMerge={handleFetchMerge}
          handleReCAPTCHA={handleReCAPTCHA}
        />
      )}
      {account && balance && (
        <BalanceModal
          isOpen={isBalanceModalOpen}
          handleOpen={handleBalanceModalOpen}
          currency="pla"
          balance={balance.pla}
        />
      )}
      <EmailVerifyModal
        isOpen={isEmailModalOpen}
        handleOpen={handleEmailModalOpen}
      />
      <MergeErrorModal
        isOpen={isMergeErrorModalOpen}
        isOwnerCheckFail={isOwnerCheckFail}
        textList={
          isOwnerCheckFail
            ? MERGE_OWNER_CHECK_ERROR_TEXT_LIST
            : MERGE_ERROR_TEXT_LIST
        }
        isShowIcon={!isOwnerCheckFail}
        handleOpen={(isOpen: boolean) => {
          setIsOwnerCheckFail(false);
          handleMergeErrorModalOpen(isOpen);
        }}
      />
      {isOwnerCheckLoading && <LoadingLayer />}
    </PageBlock>
  );
};

export default Merge;
